<template>
  <div class="signup-sheet">
    <div v-if="!hideHeader" class="signup-sheet__header">
      <div class="signup-sheet__title-wrapper">
        <h2 class="signup-sheet__title">Create Account</h2>
      </div>
      <router-link class="signup-sheet__login" :to="{ name: 'login' }"
        >Login</router-link
      >
    </div>
    <div class="sign-up signup-sheet__content">
      <form
        class="shortform-sign-up__form"
        role="form"
        novalidate
        @submit.prevent="view.start()"
      >
        <div
          v-if="showNameInput"
          class="shortform-sign-up__group signup-sheet__group"
        >
          <label
            class="shortform-sign-up__label signup-sheet__label"
            for="register_username"
            >Full Name</label
          >
          <InputWithContextMenu
            id="register_username"
            v-model="view.model.full_name"
            class="shortform-sign-up__input signup-sheet__input"
            :class="view.validation.stateClass('username')"
            type="text"
            placeholder=""
          />
          <FormError :errors="view.validation.errors" field="username" />
        </div>
        <div class="shortform-sign-up__group signup-sheet__group">
          <label
            class="shortform-sign-up__label signup-sheet__label"
            for="register_email"
            >Email Address</label
          >
          <InputWithContextMenu
            id="register_email_onboarding"
            v-model="view.model.email"
            class="shortform-sign-up__input signup-sheet__input"
            :class="view.validation.stateClass('email')"
            type="email"
            placeholder=""
            autofocus
          />
          <FormError :errors="view.validation.errors" field="email" />
        </div>
        <div
          v-if="!passwordless"
          class="shortform-sign-up__group signup-sheet__group"
        >
          <label
            class="shortform-sign-up__label signup-sheet__label"
            for="register_password"
            >Password</label
          >
          <InputWithContextMenu
            id="register_password_onboarding"
            v-model="view.model.password"
            type="password"
            autocomplete="new-password"
            placeholder=""
            class="shortform-sign-up__input signup-sheet__input"
            :class="view.validation.stateClass('password')"
          />
          <FormError :errors="view.validation.errors" field="password" />
        </div>
        <div
          v-if="!passwordless && view.model.password_score"
          class="text-muted font-italic"
        >
          <small>
            password strength:
            <span
              v-if="view.model.password_score === '4'"
              class="text-success font-weight-700"
              >strong</span
            >
            <span
              v-if="view.model.password_score === '3'"
              class="text-info font-weight-700"
              >good</span
            >
            <span
              v-if="view.model.password_score === '2'"
              class="text-warning font-weight-700"
              >medium</span
            >
            <span
              v-if="view.model.password_score === '1'"
              class="text-danger font-weight-700"
              >weak</span
            >
            <span
              v-if="view.model.password_score === '0'"
              class="text-danger font-weight-700"
              >terrible</span
            >
          </small>
        </div>
        <div
          v-if="view.validation.errors && view.validation.errors._schema"
          class="alert alert-danger"
        >
          {view.validation.errors._schema}
        </div>

        <div class="shortform-sign-up__group">
          <v-wait for="Register">
            <template v-slot:waiting>
              <div class="signup-sheet__submit btns">
                Creating Account
                <button-spinner />
              </div>
            </template>
            <button class="signup-sheet__submit btns" type="submit">
              Create Account
            </button>
          </v-wait>
        </div>
        <div class="signup-sheet__links">
          By creating an account, you agree to the
          <router-link
            :to="{ name: 'site_terms' }"
            class="black"
            target="_blank"
            >Terms of Service</router-link
          >
          and
          <router-link
            :to="{ name: 'site_privacy' }"
            class="black"
            target="_blank"
            >Privacy Policy</router-link
          >.
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { SignupView } from '@/models/auth/models'
import InputWithContextMenu from '@/app/components/InputWithContextMenu.vue'
import FormError from '@/components/ui/FormError.vue'

withDefaults(
  defineProps<{
    view: SignupView
    showNameInput: boolean
    hideHeader?: boolean
    passwordless?: boolean
  }>(),
  {
    hideHeader: false,
    passwordless: false,
  },
)
</script>

<style lang="scss" scoped>
.signup-sheet {
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 13px 24px 13px 24px;
    border-radius: 20px 20px 0px 0px;
  }

  &__title {
    font-size: 18px;
    line-height: 30px;

    &-wrapper {
      display: flex;
      gap: 12px;
      align-items: center;
    }
  }

  &__login {
    border: 1px solid $c-black-6;
    padding: 9px 20px 7px 20px;
    border-radius: 53px;

    font-size: 14px;
    line-height: 18px;
    text-align: center;

    @include hover {
      border-color: $c-yellow;
    }
  }
  &__content {
    background: $c-white;
    padding: 48px 24px 0px 24px;
  }

  &__input {
    background-color: $c-bg;
    border: 1px solid $c-border-7;
  }

  &__label {
    font-size: 18px;
    line-height: 20px;
    letter-spacing: -0.02em;
    font-weight: 400;
    font-weight: 500;
    margin-bottom: 8px;
  }

  &__close-icon {
    font-size: 24px;
  }

  &__submit {
    width: 100%;
    margin-top: 18px;
    padding: 18px 26px 16px 26px;
    font-size: 18px;
    line-height: 22px;
  }

  &__group {
    margin-bottom: 6px;
  }

  &__links {
    font-size: 12px;
    line-height: 16px;
    text-align: center;
    max-width: 280px;
    margin-inline: auto;
    color: $c-black-6;
    margin-top: 28px;

    padding-inline: 12px;
    @media only screen and (max-width: $w-medium-s) {
      margin-top: 32px;
      padding-inline: 0px;
    }

    a {
      color: $c-black-3;
    }
  }
}
</style>
